import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, X } from "lucide-react";
import { SectionHeading } from "@/components/shared/SectionHeading";
import { RevealStagger, RevealItem } from "@/components/shared/Reveal";

const VIDEOS = [
  {
    slug: "rescue-driver",
    title: "The Rescue Driver",
    tag: "Promo",
    description: "A driver faces a medical emergency mid-route — see how RelayRigs dispatches a verified rescue driver to keep the load moving.",
    url: "https://customer-assets-7cd3h4nn.emergentagent.net/job_landing-hub-908/artifacts/9gcuxg81_RelayRigs%20%E2%80%94%20The%20Rescue%20Driver%20v1.mp4",
    featured: true,
  },
  {
    slug: "keep-it-moving",
    title: "Keep the Trip Moving",
    tag: "How It Works",
    description: "An overview of how RelayRigs connects qualified drivers with loads, relief assignments, and HOS-aware relays.",
    url: "https://customer-assets-7cd3h4nn.emergentagent.net/job_landing-hub-908/artifacts/6e9yhe3h_cce1cb06f1ef444c84ee7e6ef30a12c9_relayrigs_final_v3.mp4",
  },
  {
    slug: "find-accept-start",
    title: "Find, Accept & Start a Load",
    tag: "How It Works",
    description: "Walk through finding an assignment, reviewing pay and requirements, accepting it, and completing a pre-trip inspection in the app.",
    url: "https://customer-assets-7cd3h4nn.emergentagent.net/job_landing-hub-908/artifacts/efrinnmt_1249f5b6c5654a63a2b0be7008e21227_relayrigs_final.mp4",
  },
  {
    slug: "secured-payments",
    title: "Secured Payments, Verified Delivery",
    tag: "How It Works",
    description: "See how funds are secured before a driver accepts a load, and how proof of delivery triggers payment release.",
    url: "https://customer-assets-7cd3h4nn.emergentagent.net/job_landing-hub-908/artifacts/5du33u58_377f6050948740e097290a6ba3906399_relayrigs-final.mp4",
  },
  {
    slug: "inspection-mode",
    title: "Inspection Mode",
    tag: "How It Works",
    description: "Organized records, controlled access — see how drivers surface everything an inspector needs in one place.",
    url: "https://customer-assets-7cd3h4nn.emergentagent.net/job_landing-hub-908/artifacts/07z5mhry_c79942ecc5d34300a57c3bea08b3cd3f_relayrigs-inspection-mode.mp4",
  },
];

export const VideoGallery = () => {
  const [active, setActive] = useState(null);

  useEffect(() => {
    if (!active) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") setActive(null);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [active]);

  return (
    <section data-testid="video-gallery-section" className="py-24 md:py-32 bg-rr-surface">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <SectionHeading eyebrow="See RelayRigs In Action" title="Watch How RelayRigs Keeps Trips Moving" testId="video-gallery" />

        <RevealStagger className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {VIDEOS.map((v) => (
            <RevealItem key={v.slug} className={v.featured ? "sm:col-span-2 lg:col-span-2" : ""}>
              <button
                onClick={() => setActive(v)}
                data-testid={`video-card-${v.slug}`}
                className={`group relative w-full text-left rounded-2xl overflow-hidden border border-rr-line bg-rr-navy ${
                  v.featured ? "aspect-[16/9]" : "aspect-[4/3]"
                }`}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-rr-navy via-rr-navySoft to-rr-orange/60 opacity-90 group-hover:opacity-100 transition-opacity" />
                <div className="relative h-full flex flex-col justify-between p-5">
                  <span className="self-start text-[10px] font-bold uppercase tracking-widest bg-white/15 text-white px-2.5 py-1 rounded-full">{v.tag}</span>
                  <div>
                    <div
                      data-testid={`video-play-button-${v.slug}`}
                      className="w-14 h-14 rounded-full bg-white/15 border border-white/30 flex items-center justify-center mb-4 group-hover:bg-rr-orange group-hover:scale-110 transition-all"
                    >
                      <Play size={22} className="text-white fill-white ml-0.5" />
                    </div>
                    <h3 className="font-heading font-bold text-white text-lg leading-tight">{v.title}</h3>
                    <p className="text-white/60 text-xs mt-1.5 leading-relaxed max-w-md">{v.description}</p>
                  </div>
                </div>
              </button>
            </RevealItem>
          ))}
        </RevealStagger>
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="video-modal"
            className="fixed inset-0 z-[1000] bg-black/85 flex items-center justify-center p-4 md:p-10"
            onClick={() => setActive(null)}
          >
            <motion.div
              initial={{ scale: 0.94, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              className="relative w-full max-w-4xl"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setActive(null)}
                data-testid="video-modal-close"
                className="absolute -top-11 right-0 text-white/80 hover:text-white flex items-center gap-1.5 text-sm font-bold"
              >
                Close <X size={18} />
              </button>
              <video
                key={active.slug}
                data-testid="video-modal-player"
                src={active.url}
                controls
                autoPlay
                className="w-full rounded-xl bg-black max-h-[80vh]"
              />
              <p className="text-white/70 text-sm mt-3">{active.title}</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
